/**
 * Commission Flow — coins flow animated between 3 nodes (Händler → Allyver → Du).
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { sceneHeader } from "./_mockHelpers.js";

export const commissionFlow: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const sub = inputs.subtext ?? "";
  const items = (inputs.items?.length ? inputs.items : ["Händler\n100€/Monat", "Allyver", "Du\n+20€/Monat"]).slice(0, 3);
  while (items.length < 3) items.push("");
  const isVertical = ctx.format === "9:16";

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const headerObj = sceneHeader({ clipPrefix, accent, eyebrow: "", title: headline, sub: sub, titlePx: lg * 0.78, subPx: sm * 0.95 });
  const nodeIds = items.map((_, i) => `${clipPrefix}-n-${i}`);
  const coinIds = [0, 1, 2, 3, 4, 5].map((i) => `${clipPrefix}-coin-${i}`);
  const pathId = `${clipPrefix}-path`;
  const path2Id = `${clipPrefix}-path2`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const renderNode = (label: string, i: number) => {
    const isCenter = i === 1;
    const lines = label.split("\n");
    return `<div id="${nodeIds[i]}" style="position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;padding:24px;width:${isVertical ? 220 : 240}px;height:${isVertical ? 220 : 240}px;border-radius:${isCenter ? "9999px" : "32px"};background:${isCenter ? `linear-gradient(135deg, ${accent} 0%, ${withAlpha(accent, 0.6)} 100%)` : `linear-gradient(165deg, ${withAlpha(themeColors.fg, 0.08)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%)`};border:1.5px solid ${isCenter ? withAlpha(accent, 0.6) : withAlpha(themeColors.fg, 0.16)};box-shadow:${isCenter ? `0 0 60px ${withAlpha(accent, 0.55)}, 0 24px 60px rgba(0,0,0,0.4)` : `0 24px 60px rgba(0,0,0,0.4), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.14)}`};color:${isCenter ? themeColors.bg : themeColors.fg};font-family:'Outfit',sans-serif;font-weight:${weights.black};text-align:center;backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);">
      ${lines.map((line, li) => `<div style="font-size:${(li === 0 ? md * 0.55 : md * 0.4)}px;letter-spacing:-0.5px;line-height:1.05;">${escapeHtml(line)}</div>`).join("")}
    </div>`;
  };

  const nodesHtml = items.map((it, i) => renderNode(it, i)).join("");

  const flowGap = isVertical ? 28 : 60;

  const coinsHtml = coinIds.map((id, i) => `<div id="${id}" style="position:absolute;width:36px;height:36px;border-radius:9999px;background:radial-gradient(circle at 35% 30%, #fde68a 0%, #f59e0b 60%, #b45309 100%);box-shadow:0 6px 16px rgba(0,0,0,0.5),inset 0 0 0 2px rgba(255,255,255,0.25);display:flex;align-items:center;justify-content:center;color:#92400e;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:18px;opacity:0;">€</div>`).join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:48px;padding:64px;">
    ${headerObj.html}
    <div id="${pathId}" style="position:relative;display:flex;flex-direction:${isVertical ? "column" : "row"};align-items:center;justify-content:center;gap:${flowGap}px;width:100%;">
      ${nodesHtml}
      ${coinsHtml}
      <!-- connector arrows -->
      <svg viewBox="0 0 100 12" preserveAspectRatio="none" style="position:absolute;top:50%;left:${isVertical ? "calc(50% - 1px)" : "20%"};${isVertical ? "transform:translateY(-50%) rotate(90deg);width:160px;height:12px;transform-origin:center;" : "transform:translateY(-50%);width:60%;height:12px;"};opacity:0.5;pointer-events:none;">
        <path id="${path2Id}" d="M0 6 L100 6" fill="none" stroke="${withAlpha(accent, 0.5)}" stroke-width="0.8" stroke-dasharray="2,3"/>
      </svg>
    </div>
  </div>`;

  const init = [
    headline ? gsapSetLine(`#${headerObj.ids.title}`, { opacity: 0, y: 16 }) : "",
    sub ? gsapSetLine(`#${headerObj.ids.sub}`, { opacity: 0, y: 12 }) : "",
    ...nodeIds.map((id, i) => gsapSetLine(`#${id}`, { opacity: 0, scale: 0.85, y: isVertical ? (i - 1) * 30 : 0, x: isVertical ? 0 : (i - 1) * 30 })),
    ...coinIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, left: "20%", top: "50%" })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (headline) tweens.push(t.to(`#${headerObj.ids.title}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.0));
  if (sub) tweens.push(t.to(`#${headerObj.ids.sub}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.2));
  nodeIds.forEach((id, i) => tweens.push(t.to(`#${id}`, { opacity: 1, scale: 1, x: 0, y: 0, duration: 0.65, ease: "raw:\"back.out(1.5)\"" }, 0.4 + i * 0.18)));
  const center = nodeIds[1] ?? `#${clipPrefix}-n-1`;
  tweens.push(t.to(`#${center}`, { scale: 1.05, duration: 0.5, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, 1.4));

  // Coin animations
  const baseStart = 1.2;
  const flowDur = Math.max(1.0, clipDurationSec - 2.2);
  coinIds.forEach((id, i) => {
    const leg = i % 2;
    const startPct = leg === 0 ? 18 : 50;
    const endPct = leg === 0 ? 50 : 82;
    const dur = 1.6 + (i % 3) * 0.2;
    const delay = baseStart + i * 0.35;
    tweens.push(`tl.set(${JSON.stringify("#" + id)},{opacity:0,${isVertical ? "top" : "left"}:"${startPct}%",${isVertical ? "left" : "top"}:"50%"},${(clipStartSec + delay - 0.01).toFixed(4)});`);
    tweens.push(`tl.to(${JSON.stringify("#" + id)},{opacity:1,duration:0.25,ease:"power1.out"},${(clipStartSec + delay).toFixed(4)});`);
    tweens.push(`tl.to(${JSON.stringify("#" + id)},{${isVertical ? "top" : "left"}:"${endPct}%",duration:${dur},ease:"power2.inOut"},${(clipStartSec + delay).toFixed(4)});`);
    tweens.push(`tl.to(${JSON.stringify("#" + id)},{opacity:0,duration:0.25,ease:"power1.in"},${(clipStartSec + delay + dur - 0.2).toFixed(4)});`);
    if (delay + dur < clipDurationSec - 0.5) {
      // Loop sequence
      tweens.push(`tl.to(${JSON.stringify("#" + id)},{repeat:${Math.max(0, Math.floor((flowDur - dur) / (dur + 0.6)))},duration:0.001},${(clipStartSec + delay + dur).toFixed(4)});`);
    }
  });

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
